import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import styled, { useTheme } from 'styled-components';

import { Controls, ControlsButton } from '@/components/Controls';
import useNetwork from '@/hooks/dq/useNetwork';

const NetworkControls = styled(Controls)`
  border-color: ${({ theme }) => theme.colors.grayscale};
  display: flex;
  margin-right: auto;

  span {
    line-height: 0;
  }
`;

const StyledReactTooltip = styled(ReactTooltip)`
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.smallBold.fontSize};
  opacity: 1 !important;
  padding: 6px 8px !important;
  font-family: ${({ theme }) => theme.typography.smallBold.fontFamily};
`;

const StyledControlsButton = styled(ControlsButton)`
  padding: 0 8px;
`;

interface Props {
  className?: string;
}

const NetworkSwitch: FC<Props> = ({ className }) => {
  // eslint-disable-next-line
  const [_, network] = useNetwork();

  const theme = useTheme();

  const navigate = useNavigate();

  return (
    <NetworkControls className={className}>
      <StyledControlsButton
        data-tip="Facebook"
        data-for="dqNetworkSwitch"
        active={network === 'facebook'}
        onClick={() => navigate('../facebook', { relative: 'path' })}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_13267_90572)">
            <circle
              cx="7.5"
              cy="7.5"
              r="7.15"
              stroke={theme.colors.gray}
              strokeWidth="0.7"
            />
            <path
              d="M15 7.5C15 3.35786 11.6421 0 7.5 0C3.35786 0 0 3.35786 0 7.5C0 11.2434 2.74263 14.3462 6.32812 14.9089V9.66797H4.42383V7.5H6.32812V5.84766C6.32812 3.96797 7.44785 2.92969 9.16099 2.92969C9.9813 2.92969 10.8398 3.07617 10.8398 3.07617V4.92188H9.89414C8.9625 4.92188 8.67188 5.50005 8.67188 6.09375V7.5H10.752L10.4194 9.66797H8.67188V14.9089C12.2574 14.3462 15 11.2434 15 7.5Z"
              fill={
                network === 'facebook' ? theme.colors.white : theme.colors.gray
              }
            />
          </g>
          <defs>
            <clipPath id="clip0_13267_90572">
              <rect width="15" height="15" fill={theme.colors.white} />
            </clipPath>
          </defs>
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="FB Reels"
        data-for="dqNetworkSwitch"
        active={network === 'reels'}
        onClick={() => navigate('../reels', { relative: 'path' })}
      >
        <svg
          width="14"
          height="15"
          viewBox="0 0 14 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_13606_98904)">
            <path
              d="M9.5 7.5L5.5 5V10L9.5 7.5Z"
              stroke={
                network === 'reels' ? theme.colors.white : theme.colors.gray
              }
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <rect
              x="0.75"
              y="1.25"
              width="12.5"
              height="12.5"
              rx="2.25"
              stroke={
                network === 'reels' ? theme.colors.white : theme.colors.gray
              }
              strokeWidth="1.5"
            />
          </g>
          <defs>
            <clipPath id="clip0_13606_98904">
              <rect
                width="14"
                height="14"
                fill="white"
                transform="translate(0 0.5)"
              />
            </clipPath>
          </defs>
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="FB Video"
        data-for="dqNetworkSwitch"
        active={network === 'facebook-video'}
        onClick={() => navigate('../facebook-video', { relative: 'path' })}
      >
        <svg
          width="20"
          height="14"
          viewBox="0 0 20 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18.7447 2.08112C18.5494 1.97018 18.3088 1.97331 18.1151 2.08893L14.3747 4.33421V2.93428C14.3747 1.72967 13.395 0.75 12.192 0.75H3.12067C1.91762 0.75 0.937988 1.72963 0.937988 2.93268V11.0666C0.937988 12.2697 1.91762 13.2493 3.12067 13.2493H12.192C13.395 13.2493 14.3747 12.2697 14.3747 11.0666V9.66672L18.1151 11.912C18.2135 11.9714 18.3244 12.0011 18.4369 12.0011C18.5432 12.0011 18.6494 11.9745 18.7447 11.9198C18.94 11.8089 19.0619 11.6011 19.0619 11.3761V2.62495C19.0619 2.39996 18.94 2.19205 18.7447 2.08112ZM13.1246 11.0666C13.1246 11.5807 12.7059 11.9994 12.1918 11.9994H3.12053C2.6065 11.9994 2.18776 11.5807 2.18776 11.0666V2.93267C2.18776 2.41864 2.6065 1.9999 3.12053 1.9999H12.1918C12.7059 1.9999 13.1246 2.41864 13.1246 2.93267V11.0666ZM17.812 10.2714L14.3745 8.20906V5.79039L17.812 3.72811V10.2714ZM10.1358 6.14197L6.88275 4.05149C6.5687 3.84994 6.17028 3.83588 5.84374 4.01399C5.5172 4.19211 5.31253 4.53584 5.31253 4.9077V9.08868C5.31253 9.4621 5.51564 9.80426 5.84374 9.98239C5.99686 10.0652 6.16404 10.1074 6.33278 10.1074C6.52495 10.1074 6.71557 10.0527 6.88431 9.94489L10.1373 7.8544C10.4311 7.66535 10.6045 7.34505 10.6045 6.99663C10.6045 6.64821 10.428 6.33102 10.1358 6.14197ZM6.56256 8.66684V5.33262L9.15622 6.99971L6.56256 8.66684Z"
            fill={
              network === 'facebook-video'
                ? theme.colors.white
                : theme.colors.gray
            }
            stroke={
              network === 'facebook-video'
                ? theme.colors.white
                : theme.colors.gray
            }
            strokeWidth="0.25"
          />
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="FB Text Status"
        data-for="dqNetworkSwitch"
        active={network === 'text-status'}
        onClick={() => navigate('../text-status', { relative: 'path' })}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.11354 11.4765L1.89022 13.3712C1.81126 13.4385 1.71499 13.4815 1.61273 13.4953C1.51047 13.509 1.40648 13.4928 1.31297 13.4487C1.21946 13.4045 1.14034 13.3342 1.0849 13.246C1.02945 13.1578 1 13.0554 1 12.9509V2.04912C1 1.90349 1.05707 1.76382 1.15865 1.66083C1.26023 1.55785 1.39801 1.5 1.54167 1.5H13.4583C13.602 1.5 13.7398 1.55785 13.8414 1.66083C13.9429 1.76382 14 1.90349 14 2.04912V10.8351C14 10.9808 13.9429 11.1204 13.8414 11.2234C13.7398 11.3264 13.602 11.3842 13.4583 11.3842H4.36314L4.11354 11.4765Z"
            stroke={
              network === 'text-status' ? theme.colors.white : theme.colors.gray
            }
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.35693 5.30078H9.64265"
            stroke={
              network === 'text-status' ? theme.colors.white : theme.colors.gray
            }
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.35693 7.5H9.64265"
            stroke={
              network === 'text-status' ? theme.colors.white : theme.colors.gray
            }
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="Instagram"
        data-for="dqNetworkSwitch"
        active={network === 'instagram'}
        onClick={() => navigate('../instagram', { relative: 'path' })}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.40795 0.045C5.20773 0.0081818 5.46273 0 7.5 0C9.53727 0 9.79227 0.00886363 10.5914 0.045C11.3905 0.0811363 11.9359 0.208636 12.4132 0.393409C12.913 0.582273 13.3664 0.8775 13.7414 1.25932C14.1232 1.63364 14.4177 2.08636 14.6059 2.58682C14.7914 3.06409 14.9182 3.60955 14.955 4.40727C14.9918 5.20841 15 5.46341 15 7.5C15 9.53727 14.9911 9.79227 14.955 10.592C14.9189 11.3898 14.7914 11.9352 14.6059 12.4125C14.4177 12.913 14.1227 13.3665 13.7414 13.7414C13.3664 14.1232 12.913 14.4177 12.4132 14.6059C11.9359 14.7914 11.3905 14.9182 10.5927 14.955C9.79227 14.9918 9.53727 15 7.5 15C5.46273 15 5.20773 14.9911 4.40795 14.955C3.61023 14.9189 3.06477 14.7914 2.5875 14.6059C2.08699 14.4177 1.63351 14.1227 1.25864 13.7414C0.877077 13.3668 0.581805 12.9136 0.393409 12.4132C0.208636 11.9359 0.0818182 11.3905 0.045 10.5927C0.0081818 9.79159 0 9.53659 0 7.5C0 5.46273 0.00886363 5.20773 0.045 4.40864C0.0811363 3.60955 0.208636 3.06409 0.393409 2.58682C0.582084 2.08642 0.877581 1.63317 1.25932 1.25864C1.63366 0.87716 2.08669 0.581893 2.58682 0.393409C3.06409 0.208636 3.60955 0.0818182 4.40727 0.045H4.40795ZM10.5307 1.395C9.73977 1.35886 9.5025 1.35136 7.5 1.35136C5.4975 1.35136 5.26023 1.35886 4.46932 1.395C3.73773 1.42841 3.34091 1.55045 3.07636 1.65341C2.72659 1.78977 2.47636 1.95136 2.21386 2.21386C1.96503 2.45594 1.77353 2.75065 1.65341 3.07636C1.55045 3.34091 1.42841 3.73773 1.395 4.46932C1.35886 5.26023 1.35136 5.4975 1.35136 7.5C1.35136 9.5025 1.35886 9.73977 1.395 10.5307C1.42841 11.2623 1.55045 11.6591 1.65341 11.9236C1.77341 12.2489 1.965 12.5441 2.21386 12.7861C2.45591 13.035 2.75114 13.2266 3.07636 13.3466C3.34091 13.4495 3.73773 13.5716 4.46932 13.605C5.26023 13.6411 5.49682 13.6486 7.5 13.6486C9.50318 13.6486 9.73977 13.6411 10.5307 13.605C11.2623 13.5716 11.6591 13.4495 11.9236 13.3466C12.2734 13.2102 12.5236 13.0486 12.7861 12.7861C13.035 12.5441 13.2266 12.2489 13.3466 11.9236C13.4495 11.6591 13.5716 11.2623 13.605 10.5307C13.6411 9.73977 13.6486 9.5025 13.6486 7.5C13.6486 5.4975 13.6411 5.26023 13.605 4.46932C13.5716 3.73773 13.4495 3.34091 13.3466 3.07636C13.2102 2.72659 13.0486 2.47636 12.7861 2.21386C12.544 1.96505 12.2493 1.77355 11.9236 1.65341C11.6591 1.55045 11.2623 1.42841 10.5307 1.395ZM6.54205 9.81205C7.07704 10.0347 7.67276 10.0648 8.22745 9.89708C8.78214 9.72936 9.2614 9.37426 9.58337 8.89244C9.90534 8.41061 10.05 7.83196 9.99276 7.2553C9.93548 6.67864 9.67978 6.13975 9.26932 5.73068C9.00766 5.46919 8.69128 5.26896 8.34295 5.14442C7.99463 5.01987 7.62302 4.97411 7.25488 5.01042C6.88675 5.04673 6.53124 5.16421 6.21396 5.35441C5.89667 5.5446 5.62551 5.80278 5.41998 6.11035C5.21445 6.41793 5.07967 6.76724 5.02535 7.13315C4.97103 7.49907 4.99851 7.87247 5.10582 8.22649C5.21313 8.5805 5.3976 8.90632 5.64595 9.18049C5.89429 9.45466 6.20034 9.67035 6.54205 9.81205ZM4.77409 4.77409C5.13206 4.41612 5.55704 4.13216 6.02475 3.93843C6.49246 3.7447 6.99375 3.64498 7.5 3.64498C8.00625 3.64498 8.50754 3.7447 8.97525 3.93843C9.44296 4.13216 9.86794 4.41612 10.2259 4.77409C10.5839 5.13206 10.8678 5.55704 11.0616 6.02475C11.2553 6.49246 11.355 6.99375 11.355 7.5C11.355 8.00625 11.2553 8.50754 11.0616 8.97525C10.8678 9.44296 10.5839 9.86794 10.2259 10.2259C9.50295 10.9489 8.52241 11.355 7.5 11.355C6.47759 11.355 5.49705 10.9489 4.77409 10.2259C4.05113 9.50295 3.64498 8.52241 3.64498 7.5C3.64498 6.47759 4.05113 5.49705 4.77409 4.77409ZM12.21 4.21909C12.2987 4.13541 12.3697 4.03478 12.4188 3.92316C12.468 3.81155 12.4942 3.69121 12.496 3.56927C12.4977 3.44733 12.475 3.32628 12.4292 3.21328C12.3833 3.10028 12.3153 2.99762 12.2291 2.91139C12.1428 2.82516 12.0402 2.75711 11.9272 2.71126C11.8142 2.66542 11.6931 2.64271 11.5712 2.64449C11.4492 2.64627 11.3289 2.67249 11.2173 2.72161C11.1057 2.77073 11.005 2.84175 10.9214 2.93045C10.7586 3.10297 10.6695 3.33213 10.673 3.56927C10.6764 3.80641 10.7722 4.03287 10.9399 4.20057C11.1076 4.36828 11.334 4.46402 11.5712 4.46748C11.8083 4.47093 12.0375 4.38183 12.21 4.21909Z"
            fill={
              network === 'instagram' ? theme.colors.white : theme.colors.gray
            }
          />
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="Threads"
        data-for="dqNetworkSwitch"
        active={network === 'threads'}
        onClick={() => navigate('../threads', { relative: 'path' })}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.8848 6.86286C10.7691 4.72929 9.60338 3.50786 7.64624 3.49571C6.46624 3.48857 5.47981 3.98857 4.87553 4.905L5.95267 5.64286C6.40553 4.95643 7.12053 4.81429 7.63838 4.81929C8.28338 4.82357 8.7691 5.01071 9.0841 5.37643C9.31267 5.64214 9.46624 6.01 9.54267 6.47357C8.97124 6.37643 8.35267 6.34643 7.69267 6.38429C5.83124 6.49143 4.63553 7.57714 4.71553 9.085C4.75624 9.85 5.13696 10.5086 5.78838 10.9386C6.33838 11.3021 7.04838 11.4793 7.78481 11.4393C8.75838 11.3857 9.52196 11.0143 10.0548 10.3357C10.4591 9.82 10.7148 9.15143 10.8277 8.31C11.292 8.59 11.6348 8.95786 11.8255 9.40071C12.1484 10.1536 12.167 11.3907 11.1577 12.3993C10.2734 13.2821 9.21053 13.665 7.6041 13.6764C5.82196 13.6636 4.4741 13.0914 3.59838 11.9779C2.77696 10.935 2.35338 9.42786 2.33767 7.5C2.35338 5.57143 2.77767 4.065 3.59838 3.02143C4.4741 1.90786 5.82196 1.33643 7.6041 1.32357C9.3991 1.33714 10.7698 1.91071 11.6798 3.03071C12.1262 3.57929 12.462 4.26929 12.6841 5.07357L13.9455 4.73786C13.6762 3.74714 13.2534 2.89357 12.6784 2.18571C11.512 0.75 9.79767 0.015 7.59981 0C5.40624 0.015 3.7191 0.753572 2.58624 2.19357C1.57838 3.47571 1.05767 5.26929 1.04053 7.505C1.05767 9.74072 1.57838 11.5243 2.58624 12.8064C3.71981 14.2464 5.41481 14.985 7.60767 15C9.55838 14.9864 10.9327 14.4757 12.0648 13.345C13.547 11.8643 13.502 10.0093 13.0141 8.87071C12.6391 7.99571 11.8927 7.295 10.8855 6.86357L10.8848 6.86286ZM7.71481 10.1179C6.8991 10.1636 6.05196 9.79786 6.00981 9.01357C5.9791 8.43214 6.4241 7.78357 7.76481 7.70643C8.37075 7.66575 8.97941 7.70756 9.5741 7.83071C9.41981 9.76071 8.51338 10.0736 7.71481 10.1179Z"
            fill={
              network === 'threads' ? theme.colors.white : theme.colors.gray
            }
          />
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="Pinterest"
        data-for="dqNetworkSwitch"
        active={network === 'pinterest'}
        onClick={() => navigate('../pinterest', { relative: 'path' })}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_13267_90427)">
            <path
              d="M0 7.50012C0 10.5712 1.84676 13.2096 4.48939 14.3696C4.4683 13.8459 4.48564 13.2172 4.61994 12.6473C4.76414 12.0385 5.58498 8.56055 5.58498 8.56055C5.58498 8.56055 5.34539 8.08166 5.34539 7.37391C5.34539 6.26244 5.98957 5.43234 6.79184 5.43234C7.47404 5.43234 7.80363 5.94475 7.80363 6.55834C7.80363 7.24412 7.36623 8.26992 7.14129 9.22002C6.95338 10.0156 7.54019 10.6645 8.32506 10.6645C9.74607 10.6645 10.7031 8.83939 10.7031 6.67693C10.7031 5.03314 9.59602 3.80279 7.58232 3.80279C5.30725 3.80279 3.88992 5.49943 3.88992 7.39459C3.88992 8.04803 4.08258 8.50881 4.38434 8.86564C4.52309 9.02953 4.54236 9.09545 4.49215 9.28365C4.45617 9.42164 4.37355 9.75387 4.33934 9.88553C4.28941 10.0755 4.13549 10.1434 3.96381 10.0733C2.91592 9.64547 2.42789 8.49791 2.42789 7.20791C2.42789 5.07738 4.22473 2.5227 7.78816 2.5227C10.6516 2.5227 12.5363 4.5948 12.5363 6.81908C12.5363 9.76125 10.9006 11.9593 8.48947 11.9593C7.67977 11.9593 6.91811 11.5216 6.65719 11.0244C6.65719 11.0244 6.22178 12.7525 6.12955 13.0862C5.97053 13.6644 5.65928 14.2424 5.37469 14.6929C6.06478 14.8969 6.78067 15.0005 7.50029 15.0004C11.6419 15.0004 15.0001 11.6425 15.0001 7.50012C15.0001 3.35795 11.6419 0 7.50029 0C3.35824 0 0 3.35795 0 7.50012Z"
              fill={
                network === 'pinterest' ? theme.colors.white : theme.colors.gray
              }
            />
          </g>
          <defs>
            <clipPath id="clip0_13267_90427">
              <rect width="15" height="15" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="Twitter X"
        data-for="dqNetworkSwitch"
        active={network === 'twitter'}
        onClick={() => navigate('../twitter', { relative: 'path' })}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M5.02361 0.699219H0.423197L5.69781 8.2185L0.346191 14.2992H2.27854L6.55014 9.44088L9.94671 14.2992H14.5775L9.1262 6.54032L14.2309 0.699219H12.2819L8.2537 5.29064L5.02361 0.699219ZM10.8142 12.8969L3.14543 2.14933H4.15687L11.8442 12.8969H10.8142Z"
            fill={
              network === 'twitter' ? theme.colors.white : theme.colors.gray
            }
          />
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="WhatsApp"
        data-for="dqNetworkSwitch"
        active={network === 'whatsapp'}
        onClick={() => navigate('../whatsapp', { relative: 'path' })}
      >
        <svg
          fill={network === 'whatsapp' ? theme.colors.white : theme.colors.gray}
          height="15"
          width="15"
          version="1.1"
          id="Layer_1"
          viewBox="0 0 308 308"
          xmlSpace="preserve"
        >
          <g id="XMLID_468_">
            <path
              id="XMLID_469_"
              d="M227.904,176.981c-0.6-0.288-23.054-11.345-27.044-12.781c-1.629-0.585-3.374-1.156-5.23-1.156
		c-3.032,0-5.579,1.511-7.563,4.479c-2.243,3.334-9.033,11.271-11.131,13.642c-0.274,0.313-0.648,0.687-0.872,0.687
		c-0.201,0-3.676-1.431-4.728-1.888c-24.087-10.463-42.37-35.624-44.877-39.867c-0.358-0.61-0.373-0.887-0.376-0.887
		c0.088-0.323,0.898-1.135,1.316-1.554c1.223-1.21,2.548-2.805,3.83-4.348c0.607-0.731,1.215-1.463,1.812-2.153
		c1.86-2.164,2.688-3.844,3.648-5.79l0.503-1.011c2.344-4.657,0.342-8.587-0.305-9.856c-0.531-1.062-10.012-23.944-11.02-26.348
		c-2.424-5.801-5.627-8.502-10.078-8.502c-0.413,0,0,0-1.732,0.073c-2.109,0.089-13.594,1.601-18.672,4.802
		c-5.385,3.395-14.495,14.217-14.495,33.249c0,17.129,10.87,33.302,15.537,39.453c0.116,0.155,0.329,0.47,0.638,0.922
		c17.873,26.102,40.154,45.446,62.741,54.469c21.745,8.686,32.042,9.69,37.896,9.69c0.001,0,0.001,0,0.001,0
		c2.46,0,4.429-0.193,6.166-0.364l1.102-0.105c7.512-0.666,24.02-9.22,27.775-19.655c2.958-8.219,3.738-17.199,1.77-20.458
		C233.168,179.508,230.845,178.393,227.904,176.981z"
            />
            <path
              id="XMLID_470_"
              d="M156.734,0C73.318,0,5.454,67.354,5.454,150.143c0,26.777,7.166,52.988,20.741,75.928L0.212,302.716
		c-0.484,1.429-0.124,3.009,0.933,4.085C1.908,307.58,2.943,308,4,308c0.405,0,0.813-0.061,1.211-0.188l79.92-25.396
		c21.87,11.685,46.588,17.853,71.604,17.853C240.143,300.27,308,232.923,308,150.143C308,67.354,240.143,0,156.734,0z
		 M156.734,268.994c-23.539,0-46.338-6.797-65.936-19.657c-0.659-0.433-1.424-0.655-2.194-0.655c-0.407,0-0.815,0.062-1.212,0.188
		l-40.035,12.726l12.924-38.129c0.418-1.234,0.209-2.595-0.561-3.647c-14.924-20.392-22.813-44.485-22.813-69.677
		c0-65.543,53.754-118.867,119.826-118.867c66.064,0,119.812,53.324,119.812,118.867
		C276.546,215.678,222.799,268.994,156.734,268.994z"
            />
          </g>
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="Bluesky"
        data-for="dqNetworkSwitch"
        active={network === 'bluesky'}
        onClick={() => navigate('../bluesky', { relative: 'path' })}
      >
        <svg
          width="15"
          height="16"
          viewBox="0 0 15 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.79722 14.4975C4.72252 14.5006 4.63521 14.5009 4.6032 14.498C4.57118 14.4952 4.49316 14.4843 4.42981 14.4738C4.36647 14.4633 4.26171 14.4398 4.19702 14.4214C4.13232 14.4031 4.01826 14.363 3.94354 14.3322C3.86881 14.3014 3.74407 14.2417 3.66633 14.1994C3.58858 14.1571 3.45762 14.0775 3.37529 14.0225C3.29295 13.9675 3.16345 13.8734 3.0875 13.8134C3.01155 13.7534 2.88931 13.6507 2.81586 13.5852C2.74241 13.5196 2.60346 13.3864 2.50709 13.2892C2.41072 13.1919 2.28312 13.0522 2.22354 12.9786C2.16396 12.9051 2.07482 12.7848 2.02545 12.7113C1.97609 12.6378 1.90506 12.5175 1.86761 12.444C1.83016 12.3704 1.77897 12.2559 1.75384 12.1893C1.72871 12.1228 1.6945 12.0084 1.67782 11.935C1.65251 11.8237 1.6475 11.7658 1.6475 11.5849C1.6475 11.3924 1.65138 11.3528 1.68221 11.2307C1.70408 11.1441 1.7429 11.0405 1.78714 10.9507C1.82576 10.8722 1.8933 10.7587 1.93721 10.6985C1.98113 10.6382 2.06488 10.5407 2.12332 10.4817C2.18176 10.4227 2.27324 10.3397 2.32659 10.2973C2.37995 10.2548 2.476 10.1855 2.54003 10.1432C2.60406 10.1009 2.72274 10.0304 2.80376 9.9865C2.88478 9.94263 3.03031 9.8729 3.12714 9.83153C3.22398 9.79016 3.39634 9.72483 3.51017 9.68635C3.624 9.64787 3.72172 9.61188 3.72732 9.60638C3.73293 9.60088 3.73382 9.57366 3.72931 9.54588C3.7211 9.49537 3.7211 9.49537 3.45394 9.48003C3.30701 9.4716 3.11112 9.45336 3.01863 9.4395C2.92614 9.42564 2.78353 9.39926 2.70172 9.38088C2.6199 9.36249 2.48602 9.32761 2.40421 9.30335C2.32239 9.2791 2.1915 9.23348 2.11335 9.20198C2.03519 9.17048 1.91322 9.11537 1.84229 9.07951C1.77136 9.04366 1.65486 8.9763 1.5834 8.92983C1.51194 8.88336 1.4069 8.80892 1.34999 8.76441C1.29307 8.7199 1.19669 8.63445 1.1358 8.57453C1.07492 8.5146 0.988095 8.41974 0.942864 8.36373C0.897633 8.30771 0.827444 8.21229 0.786889 8.15169C0.746334 8.09108 0.680189 7.97807 0.639901 7.90055C0.599612 7.82303 0.540362 7.69267 0.508234 7.61085C0.476106 7.52904 0.435907 7.4094 0.418903 7.34499C0.401898 7.28058 0.37329 7.13398 0.355327 7.01922C0.337365 6.90446 0.307931 6.6874 0.289917 6.53686C0.271904 6.38631 0.237059 6.05977 0.212484 5.8112C0.187909 5.56263 0.15294 5.18453 0.134776 4.97097C0.116612 4.75741 0.090196 4.43372 0.0760743 4.25167C0.0619525 4.06962 0.0414937 3.79177 0.0306103 3.63423C0.0197268 3.47669 0.00711702 3.24446 0.0025886 3.11816C-0.0025673 2.97437 7.48502e-05 2.82923 0.00965715 2.72987C0.0180733 2.64261 0.0363501 2.51619 0.0502724 2.44894C0.0641946 2.38169 0.0908545 2.28144 0.109517 2.22615C0.128179 2.17086 0.166238 2.08215 0.194093 2.02901C0.221948 1.97588 0.276639 1.89434 0.315629 1.84782C0.354619 1.80129 0.416762 1.7397 0.453725 1.71095C0.490688 1.68219 0.559036 1.63979 0.605609 1.61672C0.652183 1.59365 0.7293 1.56293 0.776982 1.54845C0.824663 1.53397 0.924173 1.51571 0.998116 1.50787C1.09417 1.4977 1.17372 1.49741 1.27673 1.50688C1.35603 1.51416 1.48935 1.5352 1.57301 1.55362C1.65666 1.57205 1.78623 1.60742 1.86093 1.63224C1.93563 1.65705 2.05496 1.70137 2.1261 1.73072C2.19724 1.76007 2.33694 1.8249 2.43654 1.87479C2.53615 1.92468 2.68081 2.00296 2.75803 2.04874C2.83524 2.09452 2.9633 2.17572 3.0426 2.22917C3.1219 2.28263 3.25999 2.38237 3.34947 2.45082C3.43894 2.51928 3.57282 2.62634 3.64698 2.68874C3.72114 2.75115 3.84875 2.86283 3.93056 2.93692C4.01238 3.01102 4.19679 3.1882 4.34037 3.33066C4.48394 3.47311 4.67271 3.66701 4.75985 3.76154C4.84699 3.85606 5.0055 4.03338 5.11209 4.15558C5.21868 4.27777 5.40584 4.5012 5.52801 4.65208C5.65017 4.80296 5.82002 5.01807 5.90545 5.1301C5.99088 5.24213 6.11906 5.414 6.1903 5.51203C6.26154 5.61005 6.38398 5.78192 6.4624 5.89395C6.54081 6.00598 6.66294 6.18644 6.7338 6.29497C6.80465 6.4035 6.92265 6.58969 6.99603 6.70872C7.0694 6.82775 7.1784 7.0108 7.23825 7.11549C7.2981 7.22018 7.37922 7.3677 7.41852 7.44331C7.45781 7.51892 7.49479 7.58078 7.50069 7.58078C7.50658 7.58078 7.54356 7.51892 7.58286 7.44331C7.62215 7.3677 7.70326 7.22018 7.7631 7.11549C7.82294 7.0108 7.92217 6.84325 7.9836 6.74315C8.04504 6.64305 8.15151 6.47405 8.22021 6.36759C8.28891 6.26113 8.40656 6.08523 8.48166 5.9767C8.55676 5.86817 8.68217 5.69058 8.76034 5.58205C8.83852 5.47351 8.98094 5.2816 9.07683 5.15556C9.17271 5.02953 9.35114 4.80296 9.47332 4.65208C9.59551 4.5012 9.78264 4.27777 9.88917 4.15558C9.9957 4.03338 10.1734 3.83601 10.2841 3.71698C10.3948 3.59795 10.6244 3.36584 10.7943 3.20119C10.9641 3.03653 11.1905 2.82809 11.2972 2.73798C11.4039 2.64787 11.564 2.5184 11.6529 2.45026C11.7418 2.38212 11.8795 2.28263 11.9588 2.22917C12.0381 2.17572 12.1661 2.09452 12.2433 2.04874C12.3206 2.00296 12.4652 1.92468 12.5648 1.87479C12.6644 1.8249 12.8041 1.76007 12.8753 1.73072C12.9464 1.70137 13.0657 1.65705 13.1404 1.63224C13.2151 1.60742 13.3447 1.57205 13.4284 1.55362C13.512 1.5352 13.6453 1.51416 13.7246 1.50688C13.8277 1.49741 13.9072 1.4977 14.0033 1.50787C14.0772 1.51571 14.1767 1.53388 14.2244 1.54825C14.2721 1.56262 14.3577 1.59807 14.4146 1.62703C14.4846 1.66264 14.5479 1.70858 14.6102 1.76898C14.6608 1.8181 14.7259 1.89426 14.7546 1.93823C14.7834 1.9822 14.8258 2.06241 14.8488 2.11646C14.8718 2.17052 14.9042 2.26311 14.9208 2.32222C14.9374 2.38134 14.96 2.48732 14.971 2.55774C14.982 2.62817 14.9943 2.77745 14.9983 2.88948C15.0034 3.03083 14.9972 3.22174 14.9781 3.51329C14.9629 3.74435 14.9362 4.11386 14.9187 4.33442C14.9012 4.55499 14.8719 4.90731 14.8537 5.11737C14.8355 5.32743 14.8005 5.69694 14.776 5.93851C14.7514 6.18007 14.7168 6.49516 14.699 6.6387C14.6812 6.78224 14.6518 6.98735 14.6337 7.0945C14.6156 7.20165 14.5842 7.342 14.5641 7.4064C14.5439 7.4708 14.5066 7.57659 14.4811 7.64149C14.4556 7.70639 14.4018 7.82294 14.3615 7.90049C14.3212 7.97804 14.255 8.09108 14.2145 8.15169C14.1739 8.21229 14.1037 8.30771 14.0585 8.36373C14.0133 8.41974 13.9265 8.5146 13.8656 8.57453C13.8047 8.63445 13.7083 8.7199 13.6514 8.76441C13.5945 8.80892 13.4894 8.88336 13.418 8.92983C13.3465 8.9763 13.23 9.04366 13.1591 9.07951C13.0882 9.11537 12.9662 9.17048 12.888 9.20198C12.8099 9.23348 12.6774 9.27968 12.5938 9.30464C12.5101 9.3296 12.3791 9.36413 12.3027 9.38137C12.2263 9.39862 12.0823 9.42453 11.9827 9.43896C11.8831 9.45338 11.6843 9.47198 11.541 9.48029C11.2803 9.49538 11.2803 9.49538 11.2721 9.54588C11.2676 9.57366 11.2686 9.60104 11.2744 9.60673C11.2802 9.61242 11.3488 9.63794 11.4269 9.66345C11.5049 9.68895 11.6328 9.73458 11.7111 9.76484C11.7894 9.79509 11.9116 9.84644 11.9827 9.87894C12.0539 9.91144 12.1645 9.96697 12.2285 10.0023C12.2925 10.0377 12.4009 10.1032 12.4692 10.1478C12.5375 10.1925 12.6481 10.2749 12.715 10.3309C12.7818 10.387 12.8722 10.4717 12.9159 10.5191C12.9596 10.5665 13.0287 10.651 13.0693 10.7067C13.11 10.7624 13.1681 10.8567 13.1984 10.9163C13.2287 10.9758 13.2691 11.0703 13.2882 11.1263C13.3072 11.1823 13.332 11.2818 13.3433 11.3473C13.3586 11.4361 13.3619 11.5122 13.3564 11.6465C13.3523 11.7455 13.3394 11.8695 13.3278 11.922C13.3161 11.9745 13.2885 12.0716 13.2664 12.1377C13.2442 12.2038 13.1898 12.3298 13.1454 12.4177C13.101 12.5057 13.0246 12.6378 12.9756 12.7113C12.9265 12.7848 12.8376 12.9051 12.778 12.9786C12.7183 13.0522 12.5907 13.1919 12.4943 13.2892C12.3979 13.3864 12.259 13.5196 12.1855 13.5852C12.1121 13.6507 11.9898 13.7535 11.9139 13.8137C11.8379 13.8739 11.6943 13.9765 11.5947 14.0418C11.4951 14.1071 11.3573 14.1899 11.2885 14.2258C11.2198 14.2617 11.1121 14.3117 11.0492 14.3369C10.9864 14.362 10.8817 14.3986 10.8166 14.418C10.7515 14.4375 10.638 14.4636 10.5644 14.476C10.4847 14.4893 10.365 14.4984 10.2688 14.4983C10.1739 14.4982 10.0585 14.4894 9.98948 14.4769C9.92477 14.4652 9.82582 14.4412 9.76958 14.4235C9.71335 14.4057 9.59719 14.3569 9.51145 14.3148C9.42071 14.2703 9.29637 14.1948 9.21393 14.1341C9.13605 14.0767 9.01109 13.9694 8.93626 13.8957C8.86143 13.8219 8.75371 13.7043 8.69687 13.6343C8.64004 13.5643 8.54636 13.4382 8.48871 13.3542C8.43106 13.2702 8.34195 13.1298 8.2907 13.0423C8.23945 12.9548 8.15181 12.7915 8.09595 12.6795C8.04008 12.5674 7.95964 12.3956 7.91717 12.2976C7.87471 12.1995 7.80213 12.0191 7.75588 11.8965C7.70963 11.774 7.64311 11.5935 7.60806 11.4955C7.54432 11.3173 7.54432 11.3173 7.50072 11.3173C7.45712 11.3173 7.45712 11.3173 7.38851 11.5082C7.35077 11.6133 7.28481 11.792 7.24192 11.9055C7.19902 12.019 7.12861 12.1937 7.08543 12.2938C7.04226 12.3939 6.96124 12.5674 6.9054 12.6795C6.84955 12.7915 6.76192 12.9548 6.71067 13.0423C6.65942 13.1298 6.57032 13.2702 6.51266 13.3542C6.45501 13.4382 6.36134 13.5643 6.3045 13.6343C6.24767 13.7043 6.13994 13.8219 6.06511 13.8957C5.99028 13.9694 5.86624 14.076 5.78947 14.1326C5.7127 14.1891 5.59337 14.2645 5.5243 14.3C5.45522 14.3355 5.35047 14.3821 5.29151 14.4035C5.23255 14.4249 5.12777 14.4535 5.05867 14.4671C4.98958 14.4806 4.87192 14.4943 4.79722 14.4975Z"
            fill={
              network === 'bluesky' ? theme.colors.white : theme.colors.gray
            }
          />
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="Youtube Shorts"
        data-for="dqNetworkSwitch"
        active={network === 'youtube-shorts'}
        onClick={() => navigate('../youtube-shorts', { relative: 'path' })}
      >
        <svg
          width="15"
          height="16"
          viewBox="0 0 15 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_14012_140097)">
            <path
              d="M10.6667 0.5C10.18 0.5 9.71974 0.626562 9.31641 0.851562L3.01693 4.24334C2.11693 4.72743 1.5 5.69545 1.5 6.80682C1.5 8.23523 2.51375 9.42196 3.84375 9.6606L3.05729 10.0148C2.13396 10.4955 1.5 11.4739 1.5 12.6023C1.5 14.2011 2.77 15.5 4.33333 15.5C4.82333 15.5 5.28359 15.3734 5.68359 15.1484L11.8932 11.8046C12.8432 11.3376 13.5 10.342 13.5 9.19318C13.5 7.76477 12.4862 6.57804 11.1562 6.3394L11.9427 5.98517C12.866 5.50449 13.5 4.52614 13.5 3.39773C13.5 1.79886 12.23 0.5 10.6667 0.5ZM6.16667 5.95455L9.5 8L6.16667 10.0455V5.95455Z"
              fill={
                network === 'youtube-shorts'
                  ? theme.colors.white
                  : theme.colors.gray
              }
            />
          </g>
          <defs>
            <clipPath id="clip0_14012_140097">
              <rect
                width="15"
                height="15"
                fill="white"
                transform="translate(0 0.5)"
              />
            </clipPath>
          </defs>
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="LinkedIn"
        data-for="dqNetworkSwitch"
        active={network === 'linkedin'}
        onClick={() => navigate('../linkedin', { relative: 'path' })}
      >
        <svg
          width="15"
          height="16"
          viewBox="0 0 15 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_14496_83709)">
            <path
              d="M13.8896 0.5H1.10742C0.495117 0.5 0 0.983398 0 1.58105V14.416C0 15.0137 0.495117 15.5 1.10742 15.5H13.8896C14.502 15.5 15 15.0137 15 14.4189V1.58105C15 0.983398 14.502 0.5 13.8896 0.5ZM4.4502 13.2822H2.22363V6.12207H4.4502V13.2822ZM3.33691 5.14648C2.62207 5.14648 2.04492 4.56934 2.04492 3.85742C2.04492 3.14551 2.62207 2.56836 3.33691 2.56836C4.04883 2.56836 4.62598 3.14551 4.62598 3.85742C4.62598 4.56641 4.04883 5.14648 3.33691 5.14648ZM12.7822 13.2822H10.5586V9.80176C10.5586 8.97266 10.5439 7.90332 9.40137 7.90332C8.24414 7.90332 8.06836 8.80859 8.06836 9.74316V13.2822H5.84766V6.12207H7.98047V7.10059H8.00977C8.30566 6.53809 9.03223 5.94336 10.1133 5.94336C12.3662 5.94336 12.7822 7.42578 12.7822 9.35352V13.2822Z"
              fill={
                network === 'linkedin' ? theme.colors.white : theme.colors.gray
              }
            />
          </g>
          <defs>
            <clipPath id="clip0_14496_83709">
              <rect
                width="15"
                height="15"
                fill="white"
                transform="translate(0 0.5)"
              />
            </clipPath>
          </defs>
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="TikTok"
        data-for="dqNetworkSwitch"
        active={network === 'tiktok'}
        onClick={() => navigate('../tiktok', { relative: 'path' })}
      >
        <svg
          width="15"
          height="16"
          viewBox="0 0 15 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_14496_85284)">
            <path
              d="M10.6703 0.5H8.14238V10.7174C8.14238 11.9348 7.17012 12.9348 5.96016 12.9348C4.75021 12.9348 3.77793 11.9348 3.77793 10.7174C3.77793 9.52175 4.72861 8.54346 5.89536 8.5V5.93479C3.3242 5.97825 1.25 8.08696 1.25 10.7174C1.25 13.3696 3.36741 15.5 5.98178 15.5C8.59611 15.5 10.7135 13.3478 10.7135 10.7174V5.47825C11.6642 6.17392 12.8309 6.58696 14.0625 6.60871V4.04348C12.1612 3.97826 10.6703 2.41304 10.6703 0.5Z"
              fill={
                network === 'tiktok' ? theme.colors.white : theme.colors.gray
              }
            />
          </g>
          <defs>
            <clipPath id="clip0_14496_85284">
              <rect
                width="15"
                height="15"
                fill="white"
                transform="translate(0 0.5)"
              />
            </clipPath>
          </defs>
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="MSN"
        data-for="dqNetworkSwitch"
        active={network === 'msn'}
        onClick={() => navigate('../msn', { relative: 'path' })}
      >
        <svg
          width="15"
          height="16"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.13,35c-1.75,0-3.25-1.1-3.25-3.59,0-3.85,3.32-6.77,4.82-7.6a30.41,30.41,0,0,0,4.28,2,15.67,15.67,0,0,1-7.1-13.28C10.88,7.33,13.62,5,16.09,5c2,0,6.69,2.19,6.69,10.57,0,9-4.71,19.43-10.65,19.43Z"
            fill={network === 'msn' ? theme.colors.white : theme.colors.gray}
          />
          <path
            d="M19.7,32.14c0,1.31.46,1.82,1.16,1.82,1.75,0,4.53-4.08,4.43-8.36,2.65-2.8,5.83-7.18,5.83-10.69,0-.85-.25-1.56-1.25-1.56-1.54,0-3.74,2.65-5.13,5.28s-2.61,5.84-4.28,10.12a10.88,10.88,0,0,0-.76,3.39Z"
            fill={network === 'msn' ? theme.colors.white : theme.colors.gray}
          />
        </svg>
      </StyledControlsButton>
      <StyledControlsButton
        data-tip="Reddit"
        data-for="dqNetworkSwitch"
        active={network === 'reddit'}
        onClick={() => navigate('../reddit', { relative: 'path' })}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.167 8a.831.831 0 0 0-.83.83c0 .459.372.84.83.84a.831.831 0 0 0 0-1.67zm1.843 3.647c.315 0 1.403-.038 1.976-.611a.232.232 0 0 0 0-.306.213.213 0 0 0-.306 0c-.353.363-1.126.487-1.67.487-.545 0-1.308-.124-1.671-.487a.213.213 0 0 0-.306 0 .213.213 0 0 0 0 .306c.564.563 1.652.61 1.977.61zm.992-2.807c0 .458.373.83.831.83.458 0 .83-.381.83-.83a.831.831 0 0 0-1.66 0z"
            fill={network === 'reddit' ? theme.colors.white : theme.colors.gray}
          />
          <path
            d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.828-1.165c-.315 0-.602.124-.812.325-.801-.573-1.9-.945-3.121-.993l.534-2.501 1.738.372a.83.83 0 1 0 .83-.869.83.83 0 0 0-.744.468l-1.938-.41a.203.203 0 0 0-.153.028.186.186 0 0 0-.086.134l-.592 2.788c-1.24.038-2.358.41-3.17.992-.21-.2-.496-.324-.81-.324a1.163 1.163 0 0 0-.478 2.224c-.02.115-.029.23-.029.353 0 1.795 2.091 3.256 4.669 3.256 2.577 0 4.668-1.451 4.668-3.256 0-.114-.01-.238-.029-.353.401-.181.688-.592.688-1.069 0-.65-.525-1.165-1.165-1.165z"
            fill={network === 'reddit' ? theme.colors.white : theme.colors.gray}
          />
        </svg>
      </StyledControlsButton>

      <StyledReactTooltip
        place="top"
        id="dqNetworkSwitch"
        effect="solid"
        type="dark"
      />
    </NetworkControls>
  );
};

export default NetworkSwitch;
